import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Users, BarChart3, Trophy, Building } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useManagerOrganizations } from '@/hooks/use-manager-organizations';
import { OrganizationSelector } from '@/components/manager/OrganizationSelector';
import { CustomerList } from '@/components/manager/CustomerList';
import { HierarchicalGroupList } from '@/components/manager/HierarchicalGroupList';
import { ManagerStatistics } from '@/components/manager/ManagerStatistics';
import { CompetitionsList } from '@/components/manager/competitions/CompetitionsList';

export function ManagerDashboard() {
  const { 
    organizations, 
    selectedOrganization: organization, 
    loading, 
    switchOrganization, 
    hasMultipleOrganizations 
  } = useManagerOrganizations();

  const { t, dir } = useLanguage();
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    // Several organizations and none chosen yet: let the manager pick first,
    // as on the examiner page, instead of loading the first one for them.
    const mustChoose = organizations.length > 1;
    return (
      <div className="space-y-6" dir={dir}>
        {mustChoose && (
          <OrganizationSelector
            organizations={organizations}
            selectedOrganization={null}
            onSelect={switchOrganization}
          />
        )}
        <div className="flex items-center justify-center min-h-[400px]">
          <Card>
            <CardContent className="p-8 text-center">
              <Building className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">
                {t(mustChoose ? 'dashboard.manager.dashboard.chooseOrganization.title' : 'dashboard.manager.dashboard.noOrganization.title')}
              </h3>
              <p className="text-muted-foreground">
                {t(mustChoose ? 'dashboard.manager.dashboard.chooseOrganization.description' : 'dashboard.manager.dashboard.noOrganization.description')}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={dir}>
      {/* Header */}
      <div className={`flex items-center ${hasMultipleOrganizations ? 'justify-between' : 'justify-start'}`}>
        {/* Logo and Title Section */}
        <div className="flex items-center gap-4">
          {/* Organization Logo */}
          {organization.logoUrl && (
            <div className="flex-shrink-0">
              <img 
                src={organization.logoUrl} 
                alt={organization.name}
                className="h-16 w-auto max-w-[120px] object-contain"
              />
            </div>
          )}
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {organization.name}
            </h1>
            <p className="text-muted-foreground">
              {t('dashboard.manager.dashboard.description')}
            </p>
          </div>
        </div>

        {/* Organization Selector (if multiple organizations) */}
        {hasMultipleOrganizations && (
          <OrganizationSelector
            organizations={organizations}
            selectedOrganization={organization}
            onSelect={switchOrganization}
            disabled={loading}
          />
        )}
      </div>

      {/* Manager Tabs */}
      <Card className="p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" dir={dir}>
          <TabsList className="w-full grid grid-cols-4 gap-2">
            <TabsTrigger 
              value="users" 
              className="flex items-center gap-2"
            >
              <User className="h-4 w-4" />
              {t('dashboard.manager.dashboard.tabs.users')}
            </TabsTrigger>
            <TabsTrigger 
              value="groups" 
              className="flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              {t('dashboard.manager.dashboard.tabs.groups')}
            </TabsTrigger>
            <TabsTrigger 
              value="competitions" 
              className="flex items-center gap-2"
            >
              <Trophy className="h-4 w-4" />
              {t('dashboard.manager.dashboard.tabs.competitions')}
            </TabsTrigger>
            <TabsTrigger 
              value="statistics" 
              className="flex items-center gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              {t('dashboard.manager.dashboard.tabs.statistics')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-6">
            {organization && (
              <CustomerList 
                organizationId={organization.id}
                organizationName={organization.name}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            )}
          </TabsContent>

          <TabsContent value="groups" className="space-y-6">
            {organization && (
              <HierarchicalGroupList 
                organizationId={organization.id}
                searchQuery={searchQuery}
              />
            )}
          </TabsContent>

          <TabsContent value="statistics" className="space-y-6">
            {organization && (
              <ManagerStatistics organizationId={organization.id} />
            )}
          </TabsContent>

          <TabsContent value="competitions" className="space-y-6">
            {organization && (
              <CompetitionsList organizationId={organization.id} organizationName={organization.name} organizations={organizations} />
            )}
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}